// =============================================================================
// UhcCompatBadge (#78) — surfaces the full-UHC compatibility gate verdict.
// Renders the deterministic {severity, reasons[], would_improve_with[]} the backend
// returns (scan / shopping / pantry / family). Returns null when clear.
// =============================================================================
import React from 'react';
import { CitationChip } from './Citations';

const SEV_META = {
  avoid:    { icon: '⛔', label: 'Avoid' },
  caution:  { icon: '⚠️', label: 'Caution' },
  advisory: { icon: 'ℹ️', label: 'Heads up' },
  // The backend could not run the check (uhc_compat_service.check_unavailable). Shown, never
  // hidden: no badge at all is what a CLEAR item shows.
  unavailable: { icon: '❔', label: 'Not checked' },
};

const UNAVAILABLE_TEXT =
  "We couldn't check this against your health profile just now. Please try again shortly.";

/**
 * The citation for ONE fired rule (Apple App Review Guideline 1.4.1).
 *
 * Apple rejected HHS v2.0 on 2026-07-30 for showing health guidance without citations linking
 * to their sources. The backend resolves every rule's stored provenance in one place
 * (services/citation_resolver.py) and ships it as `source_citation`; this renders it right
 * where the guidance is read, so the evidence is never more than one tap away.
 *
 * Renders nothing when there is no citation, plain text when the source is known but has no
 * URL — never a dead or invented link.
 */
function SourceCitation({ citation }) {
  if (!citation || !citation.label) return null;
  const { label, url, publisher } = citation;
  if (!url) {
    return <span className="ucb-cite" title={publisher || undefined}>Source: {label}</span>;
  }
  return (
    <span className="ucb-cite">
      {'Source: '}
      <a
        className="ucb-citeLink"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={publisher ? `${label} — ${publisher}` : label}
      >
        {label}
      </a>
    </span>
  );
}

export default function UhcCompatBadge({ compatibility }) {
  if (!compatibility || !compatibility.severity || compatibility.severity === 'clear') return null;
  const { severity, reasons = [], would_improve_with: improve = [] } = compatibility;
  const meta = SEV_META[severity] || SEV_META.advisory;

  if (severity === 'unavailable') {
    return (
      <div className="ucb-card ucb-card--unavailable" role="note" aria-label="Health compatibility: Not checked">
        <div className="ucb-header">
          <span className="ucb-badge">{meta.icon} {meta.label}</span>
          <span className="ucb-title">Compatibility with your health profile</span>
        </div>
        <div className="ucb-improve">{UNAVAILABLE_TEXT}</div>
      </div>
    );
  }

  return (
    <div className={`ucb-card ucb-card--${severity}`} role="note" aria-label={`Health compatibility: ${meta.label}`}>
      <div className="ucb-header">
        <span className="ucb-badge">{meta.icon} {meta.label}</span>
        <span className="ucb-title">Compatibility with your health profile</span>
      </div>
      {reasons.length > 0 && (
        <ul className="ucb-reasons">
          {reasons.map((r, i) => (
            <li key={i} className="ucb-reason">
              {r.guidance || r.mechanism || r.rule_key}
              <SourceCitation citation={r.source_citation} />
            </li>
          ))}
        </ul>
      )}
      {improve.length > 0 && (
        <div className="ucb-improve">More precise if your profile included: {improve.join(', ')}</div>
      )}
      {/* Apple 1.4.1 (2026-10-17): the standard 📚 Citations chip for this verdict — every fired
          rule's source in one place, PLUS the standards the check rests on. It also covers a
          reason that arrived without a `source_citation` (SourceCitation renders nothing for
          it): the chip still opens the standard citations, so no warning is ever shown with no
          source reachable. */}
      <CitationChip
        sources={reasons.map(r => r && r.source_citation).filter(Boolean)}
        featureName={_featureFor(reasons)}
        itemTitle="this compatibility check"
      />
    </div>
  );
}

// Which standard-citation set applies (xaiHelpers FEATURE_BASELINES) — by the lenses that fired.
function _featureFor(reasons) {
  const lenses = new Set((reasons || []).map(r => r && r.lens));
  if (lenses.has('allergen')) return 'Allergen Check';
  if (lenses.has('drug')) return 'Food-Drug Interaction Check';
  return 'Nutrition Compatibility Check';
}
